import { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Flex, Box } from '../../ui/Layout'
import Button from '../../ui/Button'
import { H2, Text } from '../../ui/Typography'
import { Container } from '../Intro'
import { useFormConfig } from '../../context/FormConfigContext'

const PermissionStatus = styled(Flex)`
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 12px;
  background-color: ${(props) => (props.granted ? '#e8f5e9' : '#ffebee')};
`

const HiddenFileInput = styled.input`
  display: none;
`

const Permissions = ({ onNext, onBack }) => {
  const [permissions, setPermissions] = useState({
    camera: false,
    microphone: false
  })
  const [error, setError] = useState(null)
  const fileInputRef = useRef(null)
  const { formConfig } = useFormConfig()
  const requestPermissions = async () => {
    try {
      setError(null)

      // Check if permissions are already granted using Permissions API
      const [cameraPermission, microphonePermission] = await Promise.all([
        navigator.permissions.query({ name: 'camera' }),
        navigator.permissions.query({ name: 'microphone' })
      ])

      if (cameraPermission.state === 'granted' && microphonePermission.state === 'granted') {
        setPermissions({
          camera: true,
          microphone: true
        })
        onNext()
        return
      }

      // Request camera and microphone permissions
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      })

      // Stop the stream immediately after getting permissions
      stream.getTracks().forEach((track) => track.stop())

      setPermissions({
        camera: true,
        microphone: true
      })
    } catch (err) {
      console.error('Error requesting permissions:', err)

      // Check if it's a permissions error
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError(
          'Please allow camera and microphone access in your browser settings. ' +
            "You can usually do this by clicking the camera icon in your browser's address bar."
        )
      } else {
        setError(
          'An error occurred while requesting camera and microphone access. ' +
            'Please make sure your device has a camera and microphone connected.'
        )
      }
    }
  }

  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
      // Create a blob from the file
      const blob = new Blob([file], { type: file.type })
      // Call onNext with the blob to proceed to the next step
      onNext(blob)
    }
  }

  const handleUploadClick = () => {
    fileInputRef.current.click()
  }

  // Check if we can proceed
  const canProceed = permissions.camera && permissions.microphone

  return (
    <Container>
      <Text fontSize="24px" fontWeight="600" mb="24px">
        Camera & Microphone Access
      </Text>

      <Text mb="24px">
        To record your video testimonial, we need access to your camera and microphone. Please allow access when prompted by your
        browser.
      </Text>

      <PermissionStatus granted={permissions.camera}>
        <Box>📷</Box>
        <Text>Camera: {permissions.camera ? 'Granted' : 'Not granted'}</Text>
      </PermissionStatus>

      <PermissionStatus granted={permissions.microphone}>
        <Box>🎤</Box>
        <Text>Microphone: {permissions.microphone ? 'Granted' : 'Not granted'}</Text>
      </PermissionStatus>

      {error && (
        <Text color="error" textAlign="center">
          {error}
        </Text>
      )}

      <Flex flexDirection="column" mt="24px">
        {canProceed ? (
          <Button
            backgroundColor={formConfig.buttonColor}
            large
            variant="black"
            label="Continue"
            onClick={() => onNext()}
            disabled={!canProceed}
            mb="16px"
          />
        ) : (
          <Button
            backgroundColor={formConfig.buttonColor}
            large
            variant="black"
            onClick={requestPermissions}
            disabled={canProceed}
            label="Request Permissions"
            mb="16px"
          />
        )}
        <Text textAlign="center" mb="16px" onClick={handleUploadClick} style={{ cursor: 'pointer' }}>
          or upload from file
        </Text>
        <HiddenFileInput ref={fileInputRef} type="file" accept="video/*" onChange={handleFileSelect} />
      </Flex>
    </Container>
  )
}

export default Permissions
